export type StructuredData = Record<string, unknown>;

export const SITE_URL = 'https://nexapods.com.au';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const LOCAL_BUSINESS_ID = `${SITE_URL}/#localbusiness`;

const LOGO_URL = `${SITE_URL}/images/c719b13a28cc.webp`;
const BUSINESS_IMAGE_URL = `${SITE_URL}/images/dabe9d51796d.webp`;

export const organizationSchema: StructuredData = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'Nexa Pods',
  url: `${SITE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    url: LOGO_URL,
  },
  image: BUSINESS_IMAGE_URL,
  email: 'info@nexapods.com.au',
  telephone: '+61 3 9008 6321',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    telephone: '+61 3 9008 6321',
    email: 'info@nexapods.com.au',
    areaServed: 'AU',
    availableLanguage: 'English',
  },
  sameAs: [],
};

export const localBusinessSchema: StructuredData = {
  '@type': 'LocalBusiness',
  '@id': LOCAL_BUSINESS_ID,
  name: 'Nexa Pods',
  url: `${SITE_URL}/`,
  description: 'Nexa Pods supplies premium modular living, workspace and accommodation pods for projects across Melbourne, Victoria and Australia.',
  parentOrganization: { '@id': ORGANIZATION_ID },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '218/89 High Street',
    addressLocality: 'Kew',
    addressRegion: 'VIC',
    postalCode: '3101',
    addressCountry: 'AU',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -37.8078675,
    longitude: 145.0279195,
  },
  hasMap: 'https://maps.google.com/?q=218/89+High+Street+Kew+VIC+3101+Australia',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'https://schema.org/Monday',
        'https://schema.org/Tuesday',
        'https://schema.org/Wednesday',
        'https://schema.org/Thursday',
        'https://schema.org/Friday',
      ],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  priceRange: '$$',
  image: BUSINESS_IMAGE_URL,
  logo: LOGO_URL,
  telephone: '+61 3 9008 6321',
  email: 'info@nexapods.com.au',
  areaServed: [
    { '@type': 'City', name: 'Melbourne' },
    { '@type': 'AdministrativeArea', name: 'Victoria' },
    { '@type': 'Country', name: 'Australia' },
  ],
  sameAs: [],
};

function canonicalUrl(canonicalPath: string): string {
  return canonicalPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${canonicalPath}`;
}

function buildBreadcrumbSchema(title: string, canonicalPath: string): StructuredData {
  const items: StructuredData[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_URL}/`,
    },
  ];

  if (canonicalPath !== '/') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: title.split('|')[0].trim(),
      item: canonicalUrl(canonicalPath),
    });
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl(canonicalPath)}#breadcrumb`,
    itemListElement: items,
  };
}

function pageNodes(pageJsonLd?: StructuredData | StructuredData[]): StructuredData[] {
  if (!pageJsonLd) return [];
  if (Array.isArray(pageJsonLd)) return pageJsonLd.flatMap((node) => pageNodes(node));

  const graph = pageJsonLd['@graph'];
  if (Array.isArray(graph)) {
    return graph.filter((node): node is StructuredData => Boolean(node) && typeof node === 'object');
  }

  const node = Object.fromEntries(
    Object.entries(pageJsonLd).filter(([key]) => key !== '@context'),
  );
  return Object.keys(node).length > 0 ? [node] : [];
}

interface BuildPageStructuredDataOptions {
  title: string;
  canonicalPath: string;
  pageJsonLd?: StructuredData | StructuredData[];
}

interface CreateProductSchemaOptions {
  slug: string;
  name: string;
  description: string;
  image: string;
  canonicalPath: string;
  price?: number;
  offerDescription?: string;
  availability?: 'InStock' | 'PreOrder' | 'OutOfStock';
}

export function createProductSchema({
  slug,
  name,
  description,
  image,
  canonicalPath,
  price,
  offerDescription,
  availability = 'InStock',
}: CreateProductSchemaOptions): StructuredData {
  const url = canonicalUrl(canonicalPath);
  const offers: StructuredData = {
    '@type': 'Offer',
    url,
    priceCurrency: 'AUD',
    availability: `https://schema.org/${availability}`,
  };

  if (price !== undefined) offers.price = String(price);
  if (offerDescription) offers.description = offerDescription;

  return {
    '@type': 'Product',
    '@id': `${url}#${slug}`,
    name,
    description,
    image,
    url,
    brand: { '@type': 'Brand', name: 'Nexa Pods' },
    offers,
  };
}

interface ItemListToProductGraphOptions {
  canonicalPath: string;
  defaultOfferDescription?: string;
  defaultAvailability?: 'InStock' | 'PreOrder' | 'OutOfStock';
  requireNumericPrice?: boolean;
}

function isStructuredData(value: unknown): value is StructuredData {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function itemListToProductGraph(
  itemList: StructuredData,
  {
    canonicalPath,
    defaultOfferDescription,
    defaultAvailability = 'InStock',
    requireNumericPrice = false,
  }: ItemListToProductGraphOptions,
): { '@context': string; '@graph': StructuredData[] } {
  const elements = Array.isArray(itemList.itemListElement) ? itemList.itemListElement : [];
  const products = elements.flatMap((entry) => {
    if (!isStructuredData(entry)) return [];
    const item = isStructuredData(entry.item) ? entry.item : entry;
    if (typeof item.name !== 'string' || typeof item.description !== 'string' || typeof item.image !== 'string') {
      return [];
    }

    const existingOffers = isStructuredData(item.offers) ? item.offers : {};
    const numericPrice = typeof existingOffers.price === 'number'
      ? existingOffers.price
      : typeof existingOffers.price === 'string' && existingOffers.price.trim() !== ''
        ? Number(existingOffers.price)
        : undefined;
    if (requireNumericPrice && !Number.isFinite(numericPrice)) return [];
    const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const product = createProductSchema({
      slug,
      name: item.name,
      description: item.description,
      image: item.image,
      canonicalPath,
      price: Number.isFinite(numericPrice) ? numericPrice : undefined,
      offerDescription: Object.keys(existingOffers).length === 0 ? defaultOfferDescription : undefined,
      availability: defaultAvailability,
    });

    return [{
      ...item,
      ...product,
      offers: {
        ...(product.offers as StructuredData),
        ...existingOffers,
      },
    }];
  });

  return {
    '@context': 'https://schema.org',
    '@graph': products,
  };
}

export function buildPageStructuredData({
  title,
  canonicalPath,
  pageJsonLd,
}: BuildPageStructuredDataOptions): { '@context': string; '@graph': StructuredData[] } {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationSchema,
      localBusinessSchema,
      buildBreadcrumbSchema(title, canonicalPath),
      ...pageNodes(pageJsonLd),
    ],
  };
}
